import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiClientError, setEntryToken } from "../lib/api";
import { getDeviceProfile, saveNickname } from "../lib/device-profile";
import { getGreeting } from "../lib/greeting";
import Icon from "../components/Icon";
import DeviceSyncPanel from "../components/DeviceSyncPanel";
import SettingsPanel from "../components/SettingsPanel";
import type { PublicRecipientStatus } from "@shared/types";

// Step 1 — Welcome. This is the permanent QR target: /s/<random-entry-token>.
// Shows the recipient's current status plainly, without a wall of postal
// regulations, then lets the sender start. First visit on a device asks for
// a nickname (stored locally, never sent to the server) so return visits
// can greet the sender by name instead of asking again.
export default function EntryPage() {
  const { entryToken } = useParams<{ entryToken: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<PublicRecipientStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [nickname, setNickname] = useState<string | null>(() => getDeviceProfile()?.nickname ?? null);
  const [nicknameDraft, setNicknameDraft] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  // Recomputes only when nickname itself changes (first-time submit), not on
  // every render, so the greeting doesn't change mid-visit.
  const greeting = useMemo(() => (nickname ? getGreeting(nickname) : ""), [nickname]);

  useEffect(() => {
    if (!entryToken) return;
    setEntryToken(entryToken);
    api
      .getRecipientStatus()
      .then(setStatus)
      .catch(() => setError("This link isn't working right now. Please check it's complete, or try again shortly."));
  }, [entryToken]);

  function submitNickname() {
    const trimmed = nicknameDraft.trim();
    if (!trimmed) return;
    saveNickname(trimmed);
    setNickname(trimmed);
  }

  function refreshFromSyncedProfile() {
    setNickname(getDeviceProfile()?.nickname ?? null);
  }

  if (!nickname) {
    return (
      <main id="main-content">
        <div className="hero fade-in-up">
          <div className="hero-inner">
            <div className="hero-top">
              <span className="brandmark">
                <Icon name="package" size={20} strokeWidth={1.8} />
                Send Michael Post
              </span>
            </div>
            <h1>Welcome</h1>
            <p className="hero-sub">What should we call you? This stays on this device only.</p>
          </div>
        </div>

        <div className="page page-hero">
          <div className="stack">
            <div className="card stack">
              <div className="field">
                <label htmlFor="nickname">Your name</label>
                <input
                  id="nickname"
                  value={nicknameDraft}
                  onChange={(e) => setNicknameDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitNickname()}
                  placeholder="e.g. Bruce"
                  autoFocus
                  autoComplete="given-name"
                />
              </div>
              <button className="btn btn-primary" onClick={submitNickname} disabled={!nicknameDraft.trim()}>
                Continue
              </button>
            </div>

            <p className="field-help" style={{ textAlign: "center" }}>
              Already set this up on another device?
            </p>
            <DeviceSyncPanel mode="redeem-only" startOpen onSynced={refreshFromSyncedProfile} />
          </div>
        </div>
      </main>
    );
  }

  async function start() {
    setStarting(true);
    try {
      const intent = await api.createShipmentIntent();
      navigate(`/send/${intent.id}`);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Couldn't start — please try again.");
      setStarting(false);
    }
  }

  if (showSettings) {
    return (
      <SettingsPanel
        onBack={() => setShowSettings(false)}
        onProfileChanged={(nextNickname) => setNickname(nextNickname)}
      />
    );
  }

  return (
    <main id="main-content">
      <div className="hero fade-in-up">
        <div className="hero-inner">
          <div className="hero-top">
            <span className="brandmark">
              <Icon name="package" size={20} strokeWidth={1.8} />
              Send Michael Post
            </span>
            <span className="avatar">{nickname.slice(0, 1).toUpperCase()}</span>
          </div>

          <p className="welcome-back">{greeting}</p>
          <h1>What are you sending Michael today?</h1>
          <p className="hero-sub">We'll handle the RFA and BFPO postal rules for you.</p>

          {status && (
            <span className="status-pill" style={{ marginTop: "var(--space-4)", display: "inline-flex" }}>
              <span className="status-pill-dot" style={{ background: status.receivingMail ? "#5be8ac" : "#ffb37a" }} />
              {status.establishmentName
                ? `${status.establishmentName} — ${status.receivingMail ? "receiving post" : "post paused"}`
                : status.receivingMail
                  ? "Currently receiving post"
                  : "Post is currently paused"}
            </span>
          )}
        </div>
      </div>

      <div className="page page-hero entry-action-page">
        <div className="entry-action-panel stack">
          {error && (
            <div className="banner banner-danger" role="alert">
              <span>{error}</span>
            </div>
          )}

          {!status && !error && (
            <div className="card" style={{ display: "flex", justifyContent: "center", padding: "var(--space-6)" }}>
              <div className="spinner" role="status" aria-label="Loading" />
            </div>
          )}

          {status && !status.receivingMail && (
            <div className="banner banner-warn" role="status">
              <span>{status.pausedMessage}</span>
            </div>
          )}

          <button className="btn btn-primary entry-send-button" onClick={start} disabled={!status?.receivingMail || starting}>
            <span className="entry-action-icon"><Icon name="package" size={20} strokeWidth={1.9} /></span>
            <span>{starting ? "Starting…" : "Send something"}</span>
            <span className="entry-action-arrow" aria-hidden="true">→</span>
          </button>

          {status && !status.receivingMail && (
            <p className="field-help" style={{ textAlign: "center" }}>
              Check back later, or contact Michael directly if this seems wrong.
            </p>
          )}

          <button type="button" className="btn entry-settings-button" onClick={() => setShowSettings(true)}>
            <span className="entry-settings-copy">
              <span className="entry-action-icon entry-action-icon-secondary">
                <Icon name="settings" size={20} strokeWidth={1.9} />
              </span>
              <span>
                <strong>Settings</strong>
                <small>Saved address, details and themes</small>
              </span>
            </span>
            <span className="entry-settings-chevron" aria-hidden="true">›</span>
          </button>
        </div>
      </div>
    </main>
  );
}
